import { getUser } from '../auth/authService';
import { tracked, newId } from './workTelemetry';

// Success Requests live in the Integrations microservice (same target as the
// Migration Queue). Mirrors the fetch + OIDC bearer pattern in modularityService.js.
const BASE = `${process.env.REACT_APP_INTEGRATION_MICROSERVICE_URL ?? ''}`;

async function authHeaders() {
    const user = await getUser();
    if (!user?.access_token) throw new Error('Not authenticated');
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.access_token}`,
    };
}

// FastAPI's 422 carries `detail` as a list of {loc, msg}; anything not a
// string has to be flattened or the page shows "[object Object]" (seen live
// 2026-09-22 creating a request from the work form).
const readable = (detail, status) => {
    if (typeof detail === 'string' && detail) return detail;
    if (Array.isArray(detail)) {
        const parts = detail.map((d) => (d && typeof d === 'object'
            ? `${(d.loc || []).slice(-1).join('.') || 'body'}: ${d.msg || JSON.stringify(d)}` : String(d)));
        if (parts.length) return `HTTP ${status} — ${parts.join('; ')}`;
    }
    if (detail && typeof detail === 'object') return `HTTP ${status} — ${JSON.stringify(detail).slice(0, 300)}`;
    return `HTTP ${status}`;
};

async function parseError(res) {
    try {
        const body = await res.json();
        return readable(body?.detail, res.status);
    } catch {
        const text = await res.text().catch(() => '');
        if (!text || text.trimStart().startsWith('<')) return `HTTP ${res.status}`;
        return text.slice(0, 300);
    }
}

async function request(path, options = {}) {
    const headers = { ...(await authHeaders()), ...(options.headers || {}) };
    const res = await fetch(`${BASE}${path}`, { ...options, headers });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json();
}

const post = (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) });
const put = (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body) });
const del = (path, body) =>
    request(path, { method: 'DELETE', ...(body ? { body: JSON.stringify(body) } : {}) });

/** Best-effort display name for the signed-in user, for CreatedBy/UpdatedBy attribution. */
export const currentUserName = async () => {
    const user = await getUser();
    return user?.profile?.name || user?.profile?.email || user?.profile?.sub || 'unknown';
};

// --- CRUD ---
export const getSuccessRequests = (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.nexusClientId) params.append('nexus_client_id', filters.nexusClientId);
    const qs = params.toString();
    return request(`/success-requests${qs ? `?${qs}` : ''}`).then((d) => d.requests ?? []);
};

export const getSuccessRequest = (id) => request(`/success-requests/${id}`);

const createSuccessRequestUntracked = (body) =>
    post('/success-requests', body).then((d) => d.request);

const updateSuccessRequestUntracked = (id, body) =>
    put(`/success-requests/${id}`, body).then((d) => d.request);

const deleteSuccessRequestUntracked = (id, updatedBy) =>
    del(`/success-requests/${id}`, { updated_by: updatedBy });

// --- Attachments ---
export const getRequestFiles = (id) =>
    request(`/success-requests/${id}/files`).then((d) => d.attachments ?? []);

const uploadRequestFilesUntracked = async (id, files, uploadedBy) => {
    const user = await getUser();
    if (!user?.access_token) throw new Error('Not authenticated');
    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append('files', f));
    if (uploadedBy) formData.append('uploaded_by', uploadedBy);
    const res = await fetch(`${BASE}/success-requests/${id}/files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${user.access_token}` },
        body: formData,
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json().then((d) => d.attachments ?? []);
};

const downloadRequestFileUntracked = async (id, filename) => {
    const user = await getUser();
    if (!user?.access_token) throw new Error('Not authenticated');
    const res = await fetch(
        `${BASE}/success-requests/${id}/files/${encodeURIComponent(filename)}`,
        { headers: { Authorization: `Bearer ${user.access_token}` } },
    );
    if (!res.ok) throw new Error(await parseError(res));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const deleteRequestFileUntracked = (id, filename, updatedBy) =>
    del(`/success-requests/${id}/files/${encodeURIComponent(filename)}`, { updated_by: updatedBy })
        .then((d) => d.attachments ?? []);

// --- Notes (the update log) ---
export const getRequestNotes = (id) =>
    request(`/success-requests/${id}/notes`).then((d) => d.notes ?? []);

const createRequestNoteUntracked = (id, note, createdBy) =>
    post(`/success-requests/${id}/notes`, { note, created_by: createdBy }).then((d) => d.notes ?? []);

// Edit a note you wrote (ADO #4318); the server checks authorship and keeps
// the replaced text. Returns the request's notes, as getRequestNotes does.
const updateRequestNoteUntracked = (id, noteId, note) =>
    put(`/success-requests/${id}/notes/${noteId}`, { note }).then((d) => d.notes ?? []);

// --- Telemetry: every write above is recorded as a work_* event (services/workTelemetry.js).
export const createSuccessRequest = tracked('create', createSuccessRequestUntracked, ([body], r) => ({ resource: `request:${newId(r) || 'new'}`, details: { source: 'request', item_id: newId(r), fields: body } }));
export const updateSuccessRequest = tracked('update', updateSuccessRequestUntracked, ([id, body]) => ({ resource: `request:${id}`, details: { source: 'request', item_id: String(id), fields: body } }));
export const deleteSuccessRequest = tracked('delete', deleteSuccessRequestUntracked, ([id]) => ({ resource: `request:${id}`, details: { source: 'request', item_id: String(id) } }));
export const uploadRequestFiles = tracked('attachment_upload', uploadRequestFilesUntracked, ([id, files]) => ({ resource: `request:${id}`, details: { source: 'request', item_id: String(id), files: Array.from(files || []).map((f) => ({ name: f.name, bytes: f.size })) } }));
export const downloadRequestFile = tracked('attachment_download', downloadRequestFileUntracked, ([id, filename]) => ({ resource: `request:${id}`, details: { source: 'request', item_id: String(id), filename } }));
export const deleteRequestFile = tracked('attachment_delete', deleteRequestFileUntracked, ([id, filename]) => ({ resource: `request:${id}`, details: { source: 'request', item_id: String(id), filename } }));
export const createRequestNote = tracked('note', createRequestNoteUntracked, ([id, note]) => ({ resource: `request:${id}`, details: { source: 'request', item_id: String(id), note: String(note || '').slice(0, 1000) } }));
export const updateRequestNote = tracked('note_edit', updateRequestNoteUntracked, ([id, noteId, note]) => ({ resource: `request:${id}`, details: { source: 'request', item_id: String(id), note_id: String(noteId), note: String(note || '').slice(0, 1000) } }));
